import {
  MM_TENWH_FLAT_PHP,
  MM_TRAILER_FLAT_PHP,
  TRUCK_SPECS,
  appliedMaxLoadCap,
  resolveMaxLoad,
  resolveMinBillable,
  roundFreightTons,
  roundPeso,
  type Allocation,
  type AllocationInputs,
  type AllocationOption,
  type CostBreakdown,
  type TruckType,
} from '../types';

/**
 * Allocate a freight load and compute its billed total. Two billing modes:
 *
 *   • PER_TON (default) — single DOP rate × delivered tonnage, padded up to
 *     `numTrucks × minBillable` per truck, plus per-truck tolls. User picks the
 *     truck class and, for a trailer, its minimum (40 / 45 / 50 — see
 *     `resolveMinBillable`). Trucks are counted at the truck's max load, or at
 *     the quotation's lower max load per truck (`resolveMaxLoad`, 14ykpqtw3ua).
 *
 *   • MM_FLAT (withinMetroManila=true) — flat per-truck rate, no toll, no
 *     discount. Truck class is server-derived from `hasTwelveMeterLengths` and
 *     `totalWeightTons` (see `deriveMmTruckType`); the caller's `truckType` is
 *     ignored. Flat rate: trailer ₱17,600, 10-wheeler ₱7,500.
 *
 * Returns null when inputs are non-positive or the per-ton rate is missing on
 * the per-ton path. The server-side `FreightChargeFactory` (with
 * `FreightAllocator.ResolveMinBillable` / `ResolveMaxLoad`) mirrors this exactly — keep them in sync.
 */
export function allocateFreight(inputs: AllocationInputs): AllocationOption | null {
  // 14ykpqtvw4a — one weight figure for everything below: tons to 4 dp, as FreightChargeFactory does.
  const totalWeightTons = roundFreightTons(inputs.totalWeightTons);
  if (totalWeightTons <= 0) return null;

  const rounded = { ...inputs, totalWeightTons };
  if (rounded.withinMetroManila) {
    return allocateMmFlat(rounded);
  }
  return allocatePerTon(rounded);
}

/**
 * MM rule: a 12m line forces a trailer; otherwise weight under 20t routes to a
 * 10-wheeler, weight at or above 20t routes to a trailer.
 */
export function deriveMmTruckType(
  hasTwelveMeterLengths: boolean,
  totalWeightTons: number,
): TruckType {
  if (hasTwelveMeterLengths) return 'trailer';
  if (totalWeightTons < 20) return '10wh';
  return 'trailer';
}

/**
 * Rounds a freight amount up to the nearest ₱1,000. Applied on the PER_TON path only —
 * MM_FLAT totals are already clean multiples of the flat rate and pass through unchanged.
 * The gap between raw `total` and rounded `billedTotal` is captured on `CostBreakdown`
 * so the markup stays visible in the cabinet preview. Keep in sync with
 * `FreightAllocator.RoundUpToNearestThousand` on the server.
 */
export function roundUpToNearestThousand(value: number): number {
  return Math.ceil(value / 1000) * 1000;
}

function allocateMmFlat(inputs: AllocationInputs): AllocationOption | null {
  const { hasTwelveMeterLengths, totalWeightTons } = inputs;
  const truckType = deriveMmTruckType(hasTwelveMeterLengths, totalWeightTons);
  const spec = TRUCK_SPECS[truckType];
  if (!spec) return null;

  const numTrucks = Math.ceil(totalWeightTons / spec.maxLoad);
  const flatRatePerTruck = truckType === 'trailer' ? MM_TRAILER_FLAT_PHP : MM_TENWH_FLAT_PHP;
  const mmFlatCost = numTrucks * flatRatePerTruck;

  const alloc: Allocation = {
    truckType,
    numTrucks,
    minBillPerTruck: 0,
    maxLoadPerTruck: null, // MM flat billing ignores the cap (14ykpqtw3ua), as FreightChargeFactory does
    bookedCapacity: totalWeightTons,
    delivered: totalWeightTons,
    padding: 0,
    billingMode: 'MM_FLAT',
    flatRatePerTruck,
  };

  const cost: CostBreakdown = {
    deliveredCost: 0,
    paddingCost: 0,
    tollCost: 0,
    mmFlatCost,
    total: mmFlatCost,
    // MM flat-rate is already a clean total — no round-up. See FreightChargeFactory.BuildMetroManilaFlat.
    billedTotal: mmFlatCost,
    effectiveRatePerTon: totalWeightTons > 0 ? mmFlatCost / totalWeightTons : NaN,
  };

  return { ...alloc, cost };
}

function allocatePerTon(inputs: AllocationInputs): AllocationOption | null {
  const { totalWeightTons, truckType, rate, discount, tollPerTruck, locationMaxLoadPerTruck, trailerMinBillTons } = inputs;
  if (rate <= 0) return null;

  const spec = TRUCK_SPECS[truckType];
  if (!spec) return null;

  // 14ykpqtw3ua — the same helper the server bills through (FreightAllocator.ResolveMaxLoad). The cabinet only
  // ever passes a whole number ≥ 1 (isAllowedMaxLoadCap); anything non-positive yields no charge here and is
  // refused by the server.
  const effectiveMax = resolveMaxLoad(spec, locationMaxLoadPerTruck);
  if (effectiveMax <= 0) return null;

  // 14ykpqtvw4a — a trailer bills at the quotation's chosen minimum (40 when never chosen).
  const minBillPerTruck = resolveMinBillable(truckType, trailerMinBillTons);
  const numTrucks = Math.ceil(totalWeightTons / effectiveMax);
  const fleetFloor = numTrucks * minBillPerTruck;
  const bookedCapacity = Math.max(totalWeightTons, fleetFloor);
  const padding = Math.max(0, bookedCapacity - totalWeightTons);

  // Each line to the centavo before summing — the server does the same (FreightChargeFactory), so the
  // rows shown, the total shown and the server's ComputedAmount are one number.
  const deliveredCost = roundPeso(totalWeightTons * (rate - discount));
  const paddingCost = roundPeso(padding * rate);
  const tollCost = roundPeso(numTrucks * tollPerTruck);
  const total = roundPeso(deliveredCost + paddingCost + tollCost);

  const alloc: Allocation = {
    truckType,
    numTrucks,
    minBillPerTruck,
    maxLoadPerTruck: appliedMaxLoadCap(spec, locationMaxLoadPerTruck, totalWeightTons), // only when it added trucks
    bookedCapacity,
    delivered: totalWeightTons,
    padding,
    billingMode: 'PER_TON',
    flatRatePerTruck: null,
  };

  const cost: CostBreakdown = {
    deliveredCost,
    paddingCost,
    tollCost,
    mmFlatCost: 0,
    total,
    billedTotal: roundUpToNearestThousand(total),
    effectiveRatePerTon: totalWeightTons > 0 ? total / totalWeightTons : NaN,
  };

  return { ...alloc, cost };
}
